import { useState } from "react";
// @mui
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
// component
import ContentBox from "~src/components/ContentBox";
// type
import { Product } from "~src/types/brand";
// utils
import { truncateText } from "~src/utils/string";
//hooks
import useAuth from "~src/hooks/useAuth";
import Iconify from "~src/components/Iconify";
import { featureProduct, unFeatureProduct, addToTop30, removeFromTop30 } from "~src/api/products";
import { logError } from "~src/utils/logger";
import Image from "~src/components/Image";
import {
  handleAliExpressIconClick,
  handleAlibabaIconClick,
} from "~src/utils/supplierSearch";

// -------------------------------------------------------------------------

export default function ProductTile({
  product,
  brandName,
}: {
  product: Product;
  brandName: string;
}) {
  const { user } = useAuth();
  const is_superuser = user?.is_superuser;

  const [currentProduct, setCurrentProduct] = useState<Product>(product);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [addToTop30Loading, setAddToTop30Loading] = useState(false);
  // TODO: Replace with actual API data when available
  const [isInTop30, setIsInTop30] = useState(false);
  const theme = useTheme();

  const handleFeatureProduct = () => {
    setButtonLoading(true);
    featureProduct(product.product_variant_id)
      .then(() => {
        setCurrentProduct({ ...currentProduct, is_featured: true });
        setButtonLoading(false);
      })
      .catch((e) => {
        logError("catch " + e);
        setButtonLoading(false);
      });
  };

  const handleUnFeatureProduct = () => {
    setButtonLoading(true);
    unFeatureProduct(String(product.product_variant_id))
      .then(() => {
        setCurrentProduct({ ...currentProduct, is_featured: false });
        setButtonLoading(false);
      })
      .catch((e) => {
        logError("catch "+ e);
        setButtonLoading(false);
      });
  };

  const handleAddToTop30 = async () => {
    setAddToTop30Loading(true);
    
    try {
      if (isInTop30) {
        // Remove from Top 30
        await removeFromTop30(Number(product.product_variant_id));
        setIsInTop30(false);
      } else {
        // Add to Top 30
        await addToTop30(Number(product.product_variant_id));
        setIsInTop30(true);
      }
    } catch (error) {
      logError("Error toggling Top 30 status:", error);
      // Don't change the state if there's an error
    } finally {
      setAddToTop30Loading(false);
    }
  };

  return (
    <ContentBox
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "&:hover": { cursor: "pointer" },
      }}
    >
      {/* Product Image */}
      <ProductImage
        imageUrl={currentProduct.product_image_url}
        itemName={currentProduct.product_name}
      />

      {/* Product Details */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <ProductTitle
          productName={currentProduct.product_name}
          brandName={brandName}
          url={currentProduct.product_url}
        />
        <PriceDetails
          priceInUSD={currentProduct.current_price}
          price={currentProduct.current_price}
        />
      </Box>

      {is_superuser && (
        <Box sx={{ display: "flex", gap: "10px", mb: 1 }}>
          {/* AliExpress Icon Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 35,
              height: 35,
              borderRadius: "50%",
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: theme.palette.grey[300],
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
            onClick={() =>
              handleAliExpressIconClick(currentProduct.product_name)
            }
          >
            <Iconify icon={"arcticons:aliexpress"} width={24} height={24} />
          </Box>

          {/* Alibaba Icon Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 35,
              height: 35,
              borderRadius: "50%",
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: theme.palette.grey[300],
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
            onClick={() => handleAlibabaIconClick(currentProduct.product_name)}
          >
            <Iconify
              icon={"ant-design:alibaba-outlined"}
              width={24}
              height={24}
            />
          </Box>
        </Box>
      )}

      {/* Feature Product Button */}
      {is_superuser && (
        <Button
          sx={{
            mb: 0,
            borderRadius: "8px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: theme.palette.grey[300],
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            },
            "&:active": { transform: "scale(0.98)" },
          }}
          size="large"
          variant="outlined"
          startIcon={
            !buttonLoading && (
              <Iconify
                icon={
                  currentProduct?.is_featured
                    ? "material-symbols:star"
                    : "material-symbols:star-outline"
                }
                sx={{
                  color: currentProduct?.is_featured ? "#ff9800" : "inherit",
                }}
              />
            )
          }
          onClick={
            currentProduct?.is_featured
              ? handleUnFeatureProduct
              : handleFeatureProduct
          }
        >
          {buttonLoading ? (
            <CircularProgress size={30} />
          ) : currentProduct?.is_featured ? (
            "Featured"
          ) : (
            "Feature Product"
          )}
        </Button>
      )}

      {/* Add to Top 30 Button - Admin Only */}
      {is_superuser && (
        <Button
          sx={{
            mb: 0,
            mt: 1,
            borderRadius: "8px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: theme.palette.grey[300],
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            },
            "&:active": { transform: "scale(0.98)" },
          }}
          size="large"
          variant="outlined"
          onClick={handleAddToTop30}
          startIcon={
            !addToTop30Loading && (
              <Iconify
                icon={isInTop30 ? "material-symbols:remove" : "material-symbols:trending-up"}
                sx={{
                  color: isInTop30 ? theme.palette.error.main : theme.palette.primary.main,
                }}
              />
            )
          }
        >
          {addToTop30Loading ? (
            <CircularProgress size={30} />
          ) : isInTop30 ? (
            "Remove from Top 30"
          ) : (
            "Add to Top 30"
          )}
        </Button>
      )}
    </ContentBox>
  );
}

const ProductImage = ({
  imageUrl,
  itemName,
}: {
  imageUrl: string;
  itemName: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignSelf: "center",
      width: "230px",
      height: "230px",
      mb: 1,
    }}
  >
    <Image
      style={{
        objectFit: "cover",
        width: "100%",
        height: "100%",
        borderRadius: "5px",
      }}
      src={imageUrl}
      alt={`${itemName}-image`}
      onError={(e) => {
        //@ts-ignore
        e.target.src =
          "/assets/illustrations/illustration_no_product_image.png";
      }}
    />
  </Box>
);

const ProductTitle = ({
  productName,
  brandName,
  url,
}: {
  productName: string;
  brandName: string;
  url?: string;
}) => (
  <Box
    component={RouterLink}
    to={url || ""}
    target="_blank"
    sx={{
      color: "text.primary",
      textDecoration: "none",
    }}
  >
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 500,
        lineHeight: "21px",
        color: "grey.600",
        mb: 1,
      }}
    >
      {truncateText(productName, 33)}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "18px",
        color: "grey.600",
      }}
    >
      {truncateText(brandName, 33)}
    </Typography>
  </Box>
);

const PriceDetails = ({
  priceInUSD,
  price,
}: {
  priceInUSD: number;
  price: number;
}) => (
  <Typography
    sx={{
      fontSize: 20,
      lineHeight: "30px",
      fontWeight: 500,
      color: "#3900E5",
    }}
  >
    ${priceInUSD?.toLocaleString() || price?.toLocaleString()}
  </Typography>
);
